import React, { isValidElement, useMemo, useRef } from "react";
import "./LeafTable.scss";
import useWindowSize from "../../hooks/useWindowSize";
import LeafRectangleLoader from "../LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";

export const STYLES = {
  DEFAULT: "default",
  DEFAULT_WITH_BORDER: "defaultWithBorders",
  CLEAR: "clear",
  GREY: "grey",
} as const;

export const ALIGN = {
  LEFT: "left",
  RIGHT: "right",
  CENTER: "center",
} as const;

export enum BACKGROUND_COLORS {
  GREEN = "green",
  RED = "red",
  CLEAR = "clear",
  GREY = "grey",
  GREY_DARK = "greyDark",
  ORANGE = "orange",
  PURPLE = "purple",
  DARK_PURPLE = "darkPurple",
}

export enum HEADER_BACKGROUND_COLORS {
  DEFAULT = "default",
  BLACK = "black",
  DARK_PURPLE = "darkPurple",
}

export type TableHeader = {
  value: React.ReactNode | string;
  width?: string;
  padding?: string;
  headerStyle?: typeof STYLES.DEFAULT | typeof STYLES.CLEAR;
  columnAlign?: typeof ALIGN.LEFT | typeof ALIGN.RIGHT | typeof ALIGN.CENTER;
};

export type RowColumn = {
  value: React.ReactNode | string;
  height?: string;
  minWidth?: string;
  padding?: string;
  columnAlign?: typeof ALIGN.LEFT | typeof ALIGN.RIGHT | typeof ALIGN.CENTER;
  backgroundColor?: BACKGROUND_COLORS;
  headerStyle?: typeof STYLES.DEFAULT | typeof STYLES.DEFAULT_WITH_BORDER | typeof STYLES.CLEAR | typeof STYLES.GREY;
  horizontalHeader?: boolean;
  verticalHeaders?: boolean;
  ariaLabel?: string;
};

export type TableRow = {
  row: RowColumn[];
};

export type LeafTableProps = {
  header: TableHeader[];
  rows: TableRow[];
  columnAlign?: typeof ALIGN.LEFT | typeof ALIGN.RIGHT | typeof ALIGN.CENTER;
  headerStyle?: typeof STYLES.DEFAULT | typeof STYLES.CLEAR | typeof STYLES.GREY;
  hideHeader?: boolean;
  rowHeight?: string;
  ariaLabel?: string;
  combinedCells?: Map<string, Set<string>>;
  combinedCellBorder?: boolean;
  onColumnsSizeChange?: Function;
  isLoading?: boolean;
  loaderRowCount?: number;
  loaderColumnCount?: number;
  hasRoundedCorners?: boolean;
  hasBorder?: boolean;
  hasStripedRows?: boolean;
  evenColumnSpacing?: boolean;
  hasVerticalDividers?: boolean;
  headerBackgroundColor?: HEADER_BACKGROUND_COLORS;
  useHead?: boolean;
  appendRow?: React.ReactNode;
};

const getHeaderStyle = (hideHeader: boolean, headerStyle: string, headerStyleCell?: string): string => {
  if (hideHeader) return "leaf-table__header__hidden";

  const style = headerStyleCell || headerStyle;

  switch (style) {
    case STYLES.DEFAULT:
      return "leaf-table__header-default";
    case STYLES.DEFAULT_WITH_BORDER:
      return "leaf-table__header-default--with-borders";
    case STYLES.CLEAR:
      return "leaf-table__header-clear";
    case STYLES.GREY:
      return "leaf-table__header-grey";
    default:
      return "leaf-table__header-default";
  }
};

const LeafTable = ({
  header,
  rows,
  columnAlign,
  hideHeader = false,
  rowHeight = "3rem",
  headerStyle = STYLES.DEFAULT,
  ariaLabel,
  combinedCells,
  combinedCellBorder,
  onColumnsSizeChange,
  isLoading = false,
  loaderRowCount = 1,
  loaderColumnCount = 4,
  hasRoundedCorners = false,
  hasBorder = false,
  hasStripedRows = true,
  evenColumnSpacing = false,
  hasVerticalDividers = false,
  headerBackgroundColor = HEADER_BACKGROUND_COLORS.DEFAULT,
  useHead = false,
  appendRow,
}: LeafTableProps) => {
  const rowStyle = rowHeight !== null ? { height: rowHeight } : {};
  const headersRef = useRef<HTMLTableCellElement[]>([]);
  const { width } = useWindowSize();
  const usedCombinedCells = new Set<string>();

  useMemo(() => {
    if (headersRef.current.length && onColumnsSizeChange) {
      const sizes = headersRef.current.map((header) => (header ? header.getBoundingClientRect().width : 0));
      onColumnsSizeChange(sizes);
    }
  }, [width, onColumnsSizeChange]);

  // Gets the text from a React Node
  const getTextFromReactNode = (node: React.ReactNode): string => {
    if (typeof node === "string" || typeof node === "number") {
      return node.toString();
    }

    if (isValidElement<{ children?: React.ReactNode }>(node)) {
      const children = node.props.children;
      return getTextFromReactNode(children);
    }

    if (Array.isArray(node)) {
      return node.map((child) => getTextFromReactNode(child)).join("");
    }

    return "";
  };

  // Checks if the position being checked is in the correct column
  const checkIfCorrectColumn = (positions: Set<string> | undefined, col: number): boolean => {
    for (const position of positions!) {
      const [rowStr, colStr] = position.split(",");
      const column = parseInt(colStr, 10);
      if (col === column) {
        return true;
      }
    }
    return false;
  };

  // Renders cells that should be combined within each of the columns
  const renderCombined = (rowIndex: number, colIndex: number, column: RowColumn) => {
    const colToCheck = `${rowIndex},${colIndex}`;
    const value = getTextFromReactNode(column.value);

    if (combinedCells?.has(value)) {
      if (checkIfCorrectColumn(combinedCells?.get(value), colIndex)) {
        if (combinedCells?.get(value)?.has(colToCheck) && !usedCombinedCells?.has(value + colIndex)) {
          const size = combinedCells.get(value)!.size;
          usedCombinedCells.add(value + colIndex);
          return (
            <td
              key={colIndex}
              className={`${combinedCellBorder ? "leaf-table__combined-cell" : ""}`}
              style={{
                textAlign: column.columnAlign || columnAlign,
                height: column.height,
                padding: column.padding ?? "1rem",
              }}
              rowSpan={size}
              aria-label={column.ariaLabel}
            >
              {column.value}
            </td>
          );
        }
        return <></>;
      }
    }
    return (
      <td
        key={colIndex}
        style={{
          textAlign: column.columnAlign || columnAlign,
          height: column.height,
          padding: column.columnAlign ? "1rem" : undefined,
        }}
        aria-label={column.ariaLabel}
      >
        {column.value}
      </td>
    );
  };

  const getBgColorClass = (color?: string) => {
    switch (color) {
      case BACKGROUND_COLORS.GREEN:
        return "leaf-table__row__cell-bg-green";
      case BACKGROUND_COLORS.RED:
        return "leaf-table__row__cell-bg-red";
      case BACKGROUND_COLORS.GREY:
        return "leaf-table__row__cell-bg-grey";
      case BACKGROUND_COLORS.GREY_DARK:
        return "leaf-table__row__cell-bg-greyDark";
      case BACKGROUND_COLORS.ORANGE:
        return "leaf-table__row__cell-bg-orange";
      case BACKGROUND_COLORS.PURPLE:
        return "leaf-table__row__cell-bg-purple";
      case BACKGROUND_COLORS.DARK_PURPLE:
        return "leaf-table__row__cell-bg-darkPurple";
      default:
        return "";
    }
  };

  return (
    <table
      cellSpacing="0"
      cellPadding="0"
      className={`leaf-table${hasRoundedCorners ? " leaf-table__rounded" : ""}${
        hasBorder ? " leaf-table__border" : ""
      }${hasStripedRows ? " leaf-table--striped" : ""}${evenColumnSpacing ? " leaf-table__even-columns" : ""}${
        hasVerticalDividers ? " leaf-table__vertical-dividers" : ""
      }${headerBackgroundColor === HEADER_BACKGROUND_COLORS.BLACK ? " leaf-table--black-header" : ""}${
        headerBackgroundColor === HEADER_BACKGROUND_COLORS.DARK_PURPLE ? " leaf-table--dark-purple-header" : ""
      }`}
      role="table"
      aria-label={ariaLabel}
    >
      {header.length > 0 && (
        <thead>
          <tr>
            {header.map((item, index) => (
              <th
                key={index}
                className={getHeaderStyle(hideHeader, headerStyle, item.headerStyle)}
                style={{ textAlign: item.columnAlign || columnAlign, width: item.width, padding: item.padding }}
                scope="col"
                role="columnheader"
                ref={(el) => {
                  headersRef.current[index] = el as HTMLTableCellElement;
                }}
              >
                {item.value}
              </th>
            ))}
          </tr>
        </thead>
      )}
      {useHead && !isLoading && rows.length > 0 && (
        <thead>
          <tr className="leaf-table leaf-table__row" style={rowStyle}>
            {rows[0].row.map((column, colIndex) => {
              const key = `head-col-${colIndex}`;
              const isHeader = column.verticalHeaders || column.horizontalHeader;
              const align = column.columnAlign || columnAlign;
              return (
                <React.Fragment key={key}>
                  {isHeader ? (
                    <th
                      className={getHeaderStyle(hideHeader, headerStyle, column?.headerStyle)}
                      role={column.horizontalHeader ? "columnheader" : "rowheader"}
                      style={{ textAlign: align, padding: column.padding, minWidth: column.minWidth }}
                      scope={column.horizontalHeader ? "col" : "row"}
                    >
                      {column?.value}
                    </th>
                  ) : (
                    <td
                      className={getBgColorClass(column.backgroundColor)}
                      style={{
                        textAlign: align,
                        justifyItems: align,
                        height: column.height,
                        padding: column.padding ?? (column.columnAlign ? "1rem" : undefined),
                      }}
                      aria-label={column.ariaLabel}
                    >
                      {column.value}
                    </td>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>
      )}
      <tbody>
        {isLoading &&
          Array.from({ length: loaderRowCount }).map((_, rowIndex) => (
            <tr className={"leaf-table leaf-table__row"} key={`loader-row-${rowIndex}`}>
              {Array.from({ length: loaderColumnCount }).map((_, colIndex) => (
                <td key={`loader-cell-${rowIndex}-${colIndex}`}>
                  <LeafRectangleLoader isLoading={isLoading} width="95%" height="1.5rem" margin="0rem" />
                </td>
              ))}
            </tr>
          ))}
        {!isLoading &&
          rows.slice(useHead ? 1 : 0).map((row, rowIndex) => (
            <tr className="leaf-table leaf-table__row" key={rowIndex} style={rowStyle}>
              {row.row.map((column, colIndex) => {
                const key = `row-${rowIndex}-col-${colIndex}`;
                const isHeader = column.verticalHeaders || column.horizontalHeader;
                const align = column.columnAlign || columnAlign;

                if (combinedCells) {
                  return <React.Fragment key={key}>{renderCombined(rowIndex, colIndex, column)}</React.Fragment>;
                }

                return (
                  <React.Fragment key={key}>
                    {isHeader ? (
                      <th
                        className={getHeaderStyle(hideHeader, headerStyle, column?.headerStyle)}
                        role={column.horizontalHeader ? "columnheader" : "rowheader"}
                        style={{ textAlign: align, padding: column.padding, minWidth: column.minWidth }}
                        scope={column.horizontalHeader ? "col" : "row"}
                      >
                        {column?.value}
                      </th>
                    ) : (
                      <td
                        className={getBgColorClass(column.backgroundColor)}
                        style={{
                          textAlign: align,
                          justifyItems: align,
                          height: column.height,
                          padding: column.padding ?? (column.columnAlign ? "1rem" : undefined),
                        }}
                        aria-label={column.ariaLabel}
                      >
                        {column.value}
                      </td>
                    )}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
      </tbody>
      {appendRow && <tbody>{appendRow}</tbody>}
    </table>
  );
};

export default LeafTable;
